import { ReactNode } from "react";

import { Avatar, Box, Flex, SimpleGrid, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import InfoField from "./InfoField";
import classes from "./ProfileHeader.module.css";

interface ProfileData {
  workName: string;
  workRole: string;
  workId: string;
  workEmail: string;
  workPhone: string;
  centralPhone: string;
  profileImage?: string;
  personalName: string;
  privatePhone: string;
  privateEmail: string;
  startDate: string;
  birthDate: string;
  location: string;
  contractType: string;
  contactPerson: string;
  languages: string[];
}

interface ProfileHeaderProps {
  data: ProfileData;
  editAction?: ReactNode;
}

const ProfileHeader = ({ data, editAction }: ProfileHeaderProps) => {
  const { t } = useTranslation();

  return (
    <Box className={classes.layout}>
      {/* Leva strana - Slika i poslovni podaci */}
      <Flex className={classes.workSection}>
        <Avatar className={classes.avatar} src={data.profileImage} />
        <Box className={classes.workDetails}>
          <Text fw={700} fz={28} mb={4}>
            {data.workName}
          </Text>
          <Text c="dark" fz={16} mb="lg">
            {data.workRole} | {data.workId}
          </Text>

          <InfoField label={t("profilePage.header.fields.workEmail")} value={data.workEmail} mb="md" />
          <InfoField label={t("profilePage.header.fields.workPhone")} value={data.workPhone} mb="md" />
          <InfoField label={t("profilePage.header.fields.centralPhone")} value={data.centralPhone} />
        </Box>
      </Flex>

      {/* Desna strana - Lični podaci */}
      <Box className={classes.personalSection}>
        <Flex
          className={classes.titleRow}
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align={{ base: "stretch", sm: "flex-start" }}
          mb="lg"
          gap="md"
        >
          <Text fw={700} fz={28}>
            {data.personalName}
          </Text>
          {editAction ? <Box className={classes.editAction}>{editAction}</Box> : null}
        </Flex>

        <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="xl" verticalSpacing="lg">
          <InfoField label={t("profilePage.header.fields.privatePhone")} value={data.privatePhone} />
          <InfoField label={t("profilePage.header.fields.privateEmail")} value={data.privateEmail} />
          <InfoField label={t("profilePage.header.fields.startDate")} value={data.startDate} />
          <InfoField label={t("profilePage.header.fields.birthDate")} value={data.birthDate} />
          <InfoField label={t("profilePage.header.fields.location")} value={data.location} />
          <InfoField label={t("profilePage.header.fields.contractType")} value={data.contractType} />
          <InfoField label={t("profilePage.header.fields.contactPerson")} value={data.contactPerson} />
          <InfoField
            label={t("profilePage.header.fields.languages")}
            value={
              data.languages.length > 0 ? (
                <div className={classes.languagesList}>
                  {data.languages.map((lang, index) => (
                    <div key={index}>{lang}</div>
                  ))}
                </div>
              ) : (
                "-"
              )
            }
          />
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default ProfileHeader;
